<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { CONVERSATION_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import { useTrack } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import MacroPreview from './MacroPreview.vue';
import MacroVariableModal from './MacroVariableModal.vue';

const props = defineProps({
  macro: {
    type: Object,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const isExecuting = ref(false);
const showPreview = ref(false);
const showVariableModal = ref(false);
const macroVariables = ref([]);

console.log('[MacroItem] Component loaded with macro:', props.macro);

const handleExecuteMacroClick = () => {
  console.log('[MacroItem] Execute button clicked!');
  // Always try to execute directly, backend will validate and return error if variables needed
  executeMacro({});
};

const executeMacro = async (variables = {}) => {
  try {
    isExecuting.value = true;
    console.log('[MacroItem] Executing macro with variables:', variables);
    await store.dispatch('macros/execute', {
      macroId: props.macro.id,
      conversationIds: [props.conversationId],
      variables,
    });
    useTrack(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
    useAlert(t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
    showVariableModal.value = false;
  } catch (error) {
    console.log('[MacroItem] Execution error:', error);
    console.log('[MacroItem] Error response:', error.response);
    console.log('[MacroItem] Error data:', error.response?.data);

    // Check if error is due to missing variables
    if (error.response?.data?.error === 'variables_required') {
      const requiredVariables = error.response.data.required_variables || [];
      console.log('[MacroItem] Variables required:', requiredVariables);

      if (requiredVariables.length > 0) {
        // Update macroVariables and show the modal
        console.log('[MacroItem] Showing variable modal');
        macroVariables.value = requiredVariables;
        showVariableModal.value = true;
        isExecuting.value = false;
        return;
      }
    }
    console.log('[MacroItem] Showing error alert');
    useAlert(t('MACROS.ERROR'));
  } finally {
    isExecuting.value = false;
  }
};

const handleVariableSubmit = variables => {
  executeMacro(variables);
};

const closeVariableModal = () => {
  showVariableModal.value = false;
};

const toggleMacroPreview = () => {
  showPreview.value = !showPreview.value;
};

const closeMacroPreview = () => {
  showPreview.value = false;
};
</script>

<template>
  <div>
    <div
      class="relative flex items-center justify-between leading-4 rounded-md h-10 pl-3 pr-2"
      :class="showPreview ? 'cursor-default' : 'drag-handle cursor-grab'"
    >
      <span
        class="overflow-hidden whitespace-nowrap text-ellipsis font-medium text-n-slate-12"
      >
        {{ macro.name }}
      </span>
      <div class="flex items-center gap-1 justify-end">
        <NextButton
          v-tooltip.left-start="$t('MACROS.EXECUTE.PREVIEW')"
          icon="i-lucide-info"
          slate
          faded
          xs
          @click="toggleMacroPreview"
        />
        <NextButton
          v-tooltip.left-start="$t('MACROS.EXECUTE.BUTTON_TOOLTIP')"
          icon="i-lucide-play"
          slate
          faded
          xs
          :is-loading="isExecuting"
          @click="handleExecuteMacroClick"
        />
      </div>
      <transition name="menu-slide">
        <MacroPreview
          v-if="showPreview"
          v-on-clickaway="closeMacroPreview"
          :macro="macro"
        />
      </transition>
    </div>
    <!-- Variable Input Form (shows below macro item) -->
    <MacroVariableModal
      :show="showVariableModal"
      :variables="macroVariables"
      :macro-name="macro.name"
      @close="closeVariableModal"
      @submit="handleVariableSubmit"
    />
  </div>
</template>
